export const PRIZE_TYPES = {
  PAYOUT: "PAYOUT",
  PHYSICAL: "PHYSICAL",
};

export const AWARD_STATUSES = {
  CLAIMABLE: "claimable",
  REQUESTED: "requested",
  FULFILLED: "fulfilled",
  CANCELLED: "cancelled",
  FORFEITED: "forfeited",
};

export const REQUEST_STATUSES = {
  PENDING: "pending",
  PROCESSING: "processing",
  NEEDS_ACTION: "needs_action",
  COMPLETED: "completed",
  CANCELLED: "cancelled",
};

export const FULFILLMENT_METHODS = {
  PAYPAL: "PAYPAL",
  BANK: "BANK",
  VOUCHER: "VOUCHER",
  SHIPPING: "SHIPPING",
};

export const VOUCHER_PROVIDERS = ["psn", "xbox", "amazon", "zalando", "steam"];

export function normalizePrizeType(value) {
  return String(value || "").trim().toUpperCase();
}

export function normalizeStatus(value) {
  return String(value || "").trim().toLowerCase();
}

export function getPrizeAmountLabel(award = {}, language = "de") {
  const amount = Number(award.amount ?? award.prizeAmount ?? 0);
  const currency = String(award.currency || "EUR").trim().toUpperCase();
  if (amount > 0) {
    return `${amount.toLocaleString(language === "de" ? "de-DE" : "en-US")} ${currency === "EUR" ? "€" : currency}`;
  }
  return String(award.prizeLabel || award.prizeText || award.prizeName || "-").trim() || "-";
}

export function getPrizeLabel(award = {}, language = "de") {
  const prizeType = normalizePrizeType(award.prizeType);
  if (prizeType === PRIZE_TYPES.PAYOUT) return getPrizeAmountLabel(award, language);
  return String(award.prizeLabel || award.prizeText || award.physicalPrize || award.itemName || "-").trim() || "-";
}

export function getPlacementLabel(placement, language = "de") {
  const place = Number(placement || 0);
  if (!place) return "-";
  if (language === "de") return `${place}. Platz`;
  const suffix = place === 1 ? "st" : place === 2 ? "nd" : place === 3 ? "rd" : "th";
  return `${place}${suffix} place`;
}

export function getTournamentTypeLabel(award = {}, t) {
  const raw = String(award.tournamentType || award.tournament?.type || award.cupType || award.sourceTournamentType || "").trim().toLowerCase();
  if (raw.includes("special")) return t("myPrizes.types.special");
  if (raw.includes("creator") || raw.includes("community")) return t("myPrizes.types.creator");
  if (raw.includes("evening")) return t("myPrizes.types.evening");
  if (raw.includes("nightly")) return t("myPrizes.types.nightly");
  return t("myPrizes.types.tournament");
}

export function getAwardTournamentName(award = {}, t) {
  return String(award.tournamentNameSnapshot || award.tournamentName || award.tournament?.name || "").trim() || t("myPrizes.unknownTournament");
}

export function getRequestForAward(award = {}, requests = []) {
  const requestId = String(award.fulfillmentRequestId || "").trim();
  if (requestId) {
    const byId = requests.find((request) => request.id === requestId || request.requestId === requestId);
    if (byId) return byId;
  }
  const awardId = String(award.id || award.awardId || "").trim();
  return requests.find((request) => String(request.awardId || "").trim() === awardId) || null;
}

export function getAwardForTournament(tournamentId, awards = []) {
  const expectedId = String(tournamentId || "").trim();
  if (!expectedId) return null;
  return awards.find((award) => {
    const awardTournamentId = String(award.tournamentId || award.tournament?.id || "").trim();
    return awardTournamentId === expectedId;
  }) || null;
}

export function supportsRequestPrizeUiForTournament(tournament = {}, award = null) {
  const rawType = String(
    award?.tournamentType ||
    award?.tournament?.type ||
    tournament.type ||
    tournament.tournamentType ||
    ""
  ).trim().toLowerCase();
  return rawType.includes("special") || rawType.includes("creator") || rawType.includes("community");
}

export function getTournamentPrizeCardState({ tournament = {}, tournamentId = "", awards = [], requests = [], t, language = "de" }) {
  const award = getAwardForTournament(tournamentId, awards);
  if (!award || !supportsRequestPrizeUiForTournament(tournament, award)) return null;

  const request = getRequestForAward(award, requests);
  const prizeType = normalizePrizeType(award.prizeType);
  const claimable = canCreatePrizeRequest(award, request);
  const correctable = canCorrectPrizeRequest(request);
  const statusLabel = getPrizeStatusLabel({ award, request, t });
  const prizeLabel = getPrizeLabel(award, language);
  const actionLabel = prizeType === PRIZE_TYPES.PHYSICAL
    ? t("myPrizes.actions.requestPhysical")
    : t("myPrizes.actions.requestPayout");
  const wonText = prizeType === PRIZE_TYPES.PHYSICAL
    ? t("myPrizes.tournamentCard.wonPhysical", { prize: prizeLabel })
    : t("myPrizes.tournamentCard.wonPayout", { prize: prizeLabel });

  return {
    award,
    request,
    prizeType,
    claimable,
    correctable,
    statusLabel,
    prizeLabel,
    actionLabel,
    wonText,
  };
}

export function getEffectivePrizeStatus(award = {}, request = null) {
  const requestStatus = normalizeStatus(request?.status);
  if (requestStatus) return requestStatus;
  const awardStatus = normalizeStatus(award.status);
  if (awardStatus === AWARD_STATUSES.FULFILLED) return REQUEST_STATUSES.COMPLETED;
  if (awardStatus === AWARD_STATUSES.CANCELLED || awardStatus === AWARD_STATUSES.FORFEITED) {
    return REQUEST_STATUSES.CANCELLED;
  }
  return awardStatus || AWARD_STATUSES.CLAIMABLE;
}

export function getPrizeStatusLabel({ award = {}, request = null, t }) {
  const prizeType = normalizePrizeType(award.prizeType);
  const status = getEffectivePrizeStatus(award, request);
  const prefix = prizeType === PRIZE_TYPES.PHYSICAL ? "physical" : "payout";
  return t(`myPrizes.status.${prefix}.${status}`) || status;
}

export function canCreatePrizeRequest(award = {}, request = null) {
  if (request) return false;
  return normalizeStatus(award.status) === AWARD_STATUSES.CLAIMABLE;
}

export function canCorrectPrizeRequest(request = null) {
  return normalizeStatus(request?.status) === REQUEST_STATUSES.NEEDS_ACTION;
}

export function isTerminalPrizeRequest(award = {}, request = null) {
  const status = getEffectivePrizeStatus(award, request);
  return status === REQUEST_STATUSES.COMPLETED || status === REQUEST_STATUSES.CANCELLED;
}

export function validatePayPalEmail(value) {
  const email = String(value || "").trim();
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

export const IBAN_COUNTRY_LENGTHS = Object.freeze({
  AD: 24,
  AE: 23,
  AL: 28,
  AT: 20,
  AZ: 28,
  BA: 20,
  BE: 16,
  BG: 22,
  BH: 22,
  BR: 29,
  BY: 28,
  CH: 21,
  CR: 22,
  CY: 28,
  CZ: 24,
  DE: 22,
  DK: 18,
  DO: 28,
  EE: 20,
  EG: 29,
  ES: 24,
  FI: 18,
  FO: 18,
  FR: 27,
  GB: 22,
  GE: 22,
  GI: 23,
  GL: 18,
  GR: 27,
  GT: 28,
  HR: 21,
  HU: 28,
  IE: 22,
  IL: 23,
  IQ: 23,
  IS: 26,
  IT: 27,
  JO: 30,
  KW: 30,
  KZ: 20,
  LB: 28,
  LC: 32,
  LI: 21,
  LT: 20,
  LU: 20,
  LV: 21,
  MC: 27,
  MD: 24,
  ME: 22,
  MK: 19,
  MR: 27,
  MT: 31,
  MU: 30,
  NL: 18,
  NO: 15,
  PK: 24,
  PL: 28,
  PS: 29,
  PT: 25,
  QA: 29,
  RO: 24,
  RS: 22,
  SA: 24,
  SC: 31,
  SE: 24,
  SI: 19,
  SK: 24,
  SM: 27,
  ST: 25,
  SV: 28,
  TL: 23,
  TN: 24,
  TR: 26,
  UA: 29,
  VA: 22,
  VG: 24,
  XK: 20,
});

export const IBAN_MAX_LENGTH = 34;

export function normalizeIbanForClient(value) {
  return String(value || "").replace(/\s+/g, "").toUpperCase().replace(/[^A-Z0-9]/g, "");
}

export function getIbanCountryLength(ibanOrCountry) {
  const countryCode = String(ibanOrCountry || "").trim().toUpperCase().slice(0, 2);
  return IBAN_COUNTRY_LENGTHS[countryCode] || null;
}

export function limitIbanForClient(value) {
  const iban = normalizeIbanForClient(value);
  const maxLength = getIbanCountryLength(iban) || IBAN_MAX_LENGTH;
  return iban.slice(0, maxLength);
}

export function formatIbanForDisplay(value) {
  return limitIbanForClient(value).replace(/(.{4})/g, "$1 ").trim();
}

function ibanToNumberString(iban) {
  return iban
    .slice(4)
    .concat(iban.slice(0, 4))
    .split("")
    .map((char) => {
      if (/[A-Z]/.test(char)) return String(char.charCodeAt(0) - 55);
      return char;
    })
    .join("");
}

function mod97(numberString) {
  let remainder = 0;
  for (const char of numberString) {
    remainder = (remainder * 10 + Number(char)) % 97;
  }
  return remainder;
}

export function isPlausibleIban(value) {
  const iban = normalizeIbanForClient(value);
  const expectedLength = getIbanCountryLength(iban);
  if (!expectedLength || iban.length !== expectedLength) {
    return false;
  }
  if (!/^[A-Z]{2}\d{2}[A-Z0-9]+$/.test(iban)) {
    return false;
  }
  return mod97(ibanToNumberString(iban)) === 1;
}

export function getNeedsActionReasonLabel(reason, t) {
  const key = String(reason || "unknown").trim().toLowerCase();
  return t(`myPrizes.needsActionReasons.${key}`) || t("myPrizes.needsActionReasons.unknown");
}
